import { Box, createStyles, Group, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { Button, Title } from '@repo/foundations';
import { useCallback } from 'react';
import { useCreateWorkspace } from '../../api/hooks/workspace/useCreateWorkspace';
import { useWorkspaceImage } from '../../api/hooks/workspace/useWorkspaceImage';
import { validateWorkspaceName } from '../../utils/workspaceValidation';
import { ImageUpload } from '../ImageUpload/ImageUpload';
import { AuthCard } from './AuthCard';
import { AuthButton } from './AuthLayouts/AuthButton';
import { AuthFooter } from './AuthLayouts/AuthFooter';
import { AuthLayoutBase } from './AuthLayouts/AuthLayoutBase';
import type { WorkspaceTab } from './consts';
import { WORKSPACE_TAB } from './consts';

const useStyles = createStyles(() => ({
	card: {
		width: 500,
		marginTop: 50,
	},
}));

interface WorkspaceFormType {
	workspaceName: string;
	workspaceLogo: File | null;
}

export interface AuthStepWorkspaceCreateProps {
	setActiveWorkspaceTab?: (tab: WorkspaceTab) => void;
}

export function AuthStepWorkspaceCreate({
	setActiveWorkspaceTab,
}: AuthStepWorkspaceCreateProps) {
	const { theme } = useStyles();
	const { mutateAsync, isPending } = useCreateWorkspace();
	const { mutateAsync: uploadImage } = useWorkspaceImage();

	const form = useForm<WorkspaceFormType>({
		initialValues: {
			workspaceName: '',
			workspaceLogo: null,
		},

		validate: {
			workspaceName: validateWorkspaceName,
			// Always return null for workspaceLogo, as it's optional.
			workspaceLogo: () => null,
		},
	});

	const handleSubmit = async (values: WorkspaceFormType) => {
		if (isPending) return; // Prevent double submission

		try {
			const workspace = await mutateAsync({
				data: {
					name: values.workspaceName,
				},
			});
			if (values.workspaceLogo) {
				await uploadImage({
					workspaceId: workspace.id,
					file: values.workspaceLogo,
				});
			}

			// We use `window.open` rather than `navigate` in this context in order to
			// trigger a reload which causes the workspace information to be refreshed
			// with the most up to date information about user's workspace membership.
			window.open('/onboarding/confirm', '_self');
		} catch (error) {
			// Reset loading state on error so user can retry
			throw error; // Re-throw to let form handle the error
		}
	};

	const handleFile = useCallback(
		(file: File) => {
			form.setFieldValue('workspaceLogo', file);
		},
		[form]
	);

	return (
		<AuthLayoutBase>
			<AuthCard>
				<Stack spacing="xl">
					<Title order={1}>Create a workspace</Title>
					<form
						autoComplete="off"
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: theme.spacing.md,
						}}
						onSubmit={form.onSubmit(handleSubmit)}
					>
						<Group position="left">
							<ImageUpload
								callback={handleFile}
								label="Company or Organization logo"
								placeholder="/images/auth-image/workspace-placeholder.webp"
							/>
						</Group>
						<TextInput
							autoFocus
							label="Workspace name"
							styles={{
								input: { borderRadius: theme.other.space[2], height: '44px' },
							}}
							{...form.getInputProps('workspaceName')}
						/>
						<AuthButton
							disabled={!form.isValid || isPending}
							type="submit"
							variant="primary"
							loading={isPending}
						>
							<Group align="center" position="center">
								<Box>Create workspace</Box>
							</Group>
						</AuthButton>
					</form>
				</Stack>
				{setActiveWorkspaceTab && (
					<AuthFooter>
						<Group position="right" py="md" px="2xl">
							<Button
								variant="default"
								onClick={() => setActiveWorkspaceTab(WORKSPACE_TAB.JOIN)}
							>
								Back
							</Button>
						</Group>
					</AuthFooter>
				)}
			</AuthCard>
		</AuthLayoutBase>
	);
}
